import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { triggerHaptic } from "@/utilities/haptics";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import type { BetSelection } from "@/features/betslip/types";

type UseBonusCashPillParams = {
  outcomeIds: string[];
  isMulti: boolean;
  selection?: BetSelection;
  isReviewingOrPlacing: boolean;
  isCardInactive: boolean;
};

export const useBonusCashPill = ({
  outcomeIds,
  isMulti,
  selection,
  isReviewingOrPlacing,
  isCardInactive,
}: UseBonusCashPillParams) => {
  const outcomeId = outcomeIds[0];

  const isBonusCashApplied = useBetslipStore((state) =>
    isMulti
      ? state.promoCash.multi
      : state.promoCash.singles[outcomeId] ?? false,
  );
  const stake = useBetslipStore((state) =>
    isMulti ? state.stakes.multi : state.stakes.singles[outcomeId] ?? 0,
  );
  const toggleSinglePromoCash = useBetslipStore(
    (state) => state.toggleSinglePromoCash,
  );
  const toggleMultiPromoCash = useBetslipStore(
    (state) => state.toggleMultiPromoCash,
  );
  const remainingPromoBalance = useRemainingPromoBalance();
  const hasPromoExoticPermission = useHasPermission(
    "submitPromoRacingExoticBet",
  );

  // Every leg must allow promos: a single blocked or outright leg makes
  // the whole multi ineligible for Bonus Cash, so the pill hides.
  const multiHasBlockedLeg = useBetslipStore(
    (state) =>
      isMulti &&
      state.multiLegs.some(
        (leg) => leg.blockPromotionsBets || leg.kind === "outright",
      ),
  );

  // Per-selection Bonus Cash guards.
  const blockPromos = selection?.blockPromotionsBets ?? false;
  const isOutright = selection?.kind === "outright";
  const isExotic = selection?.kind === "exotic";
  const shouldHide =
    blockPromos ||
    multiHasBlockedLeg ||
    isReviewingOrPlacing ||
    isOutright ||
    isCardInactive ||
    (isExotic && !hasPromoExoticPermission);
  const hasEnoughBalance =
    remainingPromoBalance > 0 && remainingPromoBalance >= stake;

  const show = (!shouldHide && hasEnoughBalance) || isBonusCashApplied;

  const toggle = () => {
    triggerHaptic.click();
    if (isMulti) toggleMultiPromoCash();
    else toggleSinglePromoCash(outcomeId);
  };

  return { show, isBonusCashApplied, toggle };
};
